type TargetWaiter = (element: HTMLElement) => void;

function buildRegistryKey(targetKey: string, entityId: string | null): string {
  return entityId === null ? targetKey : targetKey + '|' + entityId;
}

export class AssistantTargetRegistry {
  private readonly elements = new Map<string, HTMLElement[]>();
  private readonly waiters = new Map<string, TargetWaiter[]>();

  register(targetKey: string, entityId: string | null, element: HTMLElement): () => void {
    const registryKey = buildRegistryKey(targetKey, entityId);
    const registered = this.elements.get(registryKey) || [];
    this.elements.set(registryKey, [...registered, element]);
    const pendingWaiters = this.waiters.get(registryKey);
    if (pendingWaiters !== undefined) {
      this.waiters.delete(registryKey);
      pendingWaiters.forEach(function resolveWaiter(waiter) {
        waiter(element);
      });
    }
    const elements = this.elements;
    return function unregister() {
      const remaining = (elements.get(registryKey) || []).filter(function keepOthers(candidate) {
        return candidate !== element;
      });
      if (remaining.length === 0) {
        elements.delete(registryKey);
        return;
      }
      elements.set(registryKey, remaining);
    };
  }

  find(targetKey: string, entityId: string | null): HTMLElement | null {
    const registered = this.elements.get(buildRegistryKey(targetKey, entityId)) || [];
    const latest = registered[registered.length - 1];
    return latest === undefined ? null : latest;
  }

  waitFor(
    targetKey: string,
    entityId: string | null,
    timeoutMilliseconds: number
  ): Promise<HTMLElement | null> {
    const existing = this.find(targetKey, entityId);
    if (existing !== null) {
      return Promise.resolve(existing);
    }
    const registryKey = buildRegistryKey(targetKey, entityId);
    const waiters = this.waiters;
    return new Promise(function waitForRegistration(resolve) {
      const waiter: TargetWaiter = function onRegistered(element) {
        window.clearTimeout(timeoutHandle);
        resolve(element);
      };
      const timeoutHandle = window.setTimeout(function giveUp() {
        const remaining = (waiters.get(registryKey) || []).filter(function keep(candidate) {
          return candidate !== waiter;
        });
        waiters.set(registryKey, remaining);
        resolve(null);
      }, timeoutMilliseconds);
      waiters.set(registryKey, [...(waiters.get(registryKey) || []), waiter]);
    });
  }
}
